// SVG / HTML / Markdown / PDF / DOCX / ODT provenance metadata inspect + clean.
// Browser port of skills/remove-ai-marks/scripts/container_meta.py
import { unzipSync, zipSync } from "fflate";
import { latin1, utf8Decode, utf8Encode, containsAny, indexOfBytes } from "./bytes";
import { AI_META_HINTS, C2PA_MARKERS } from "./imageMeta";
import { cleanText, inspectText } from "./unicodeScrub";

const ALL_HINTS = [...new Set([...AI_META_HINTS, ...C2PA_MARKERS])];

export const AI_FRONTMATTER_KEYS = new Set([
  "generator", "ai", "ai_generated", "ai-generated", "claude", "anthropic",
  "openai", "gemini", "synthid", "c2pa", "content_credentials",
  "contentcredentials", "provenance", "digital_source_type",
  "digitalsourcetype", "created_with", "createdwith", "model", "llm",
]);

const AI_META_NAME_RE = /generator|ai[-_ ]?generated|claude|anthropic|openai|gemini|synthid|c2pa|content.?credential|provenance|digital.?source|aigc/i;

export function detectContainerFormat(name, u8) {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (ext === "svg") return "svg";
  if (ext === "pdf") return "pdf";
  if (ext === "docx") return "docx";
  if (ext === "odt") return "odt";
  if (ext === "html" || ext === "htm") return "html";
  if (["md", "markdown", "mdx"].includes(ext)) return "markdown";
  if (["txt", "csv", "json", "srt", "vtt"].includes(ext)) return "text";
  if (u8) {
    if (latin1(u8, 0, 4) === "%PDF") return "pdf";
    const head = latin1(u8, 0, Math.min(500, u8.length));
    if (head.trimStart().startsWith("<") && head.toLowerCase().includes("svg")) return "svg";
    if (u8[0] === 0x50 && u8[1] === 0x4b) {
      try {
        const files = Object.keys(unzipSync(u8));
        if (files.includes("word/document.xml")) return "docx";
        if (files.includes("content.xml") && files.includes("meta.xml")) return "odt";
      } catch (e) { /* not a readable zip */ }
    }
  }
  return "unknown";
}

function blobHits(u8) {
  const c2pa = containsAny(u8, C2PA_MARKERS);
  const ai = containsAny(u8, AI_META_HINTS);
  return { hasC2pa: c2pa.length > 0, hasAi: ai.length > 0 || c2pa.length > 0, hits: [...new Set([...c2pa, ...ai])] };
}

/* ------------------------------- SVG -------------------------------- */

function cleanSvgText(src) {
  const actions = [];
  let out = src;
  const drop = (re, label) => {
    const before = out;
    out = out.replace(re, "");
    if (out !== before) actions.push(label);
  };
  drop(/<metadata[\s\S]*?<\/metadata>/gi, "drop <metadata> block");
  drop(/<[a-z0-9]*:?xmpmeta[\s\S]*?<\/[a-z0-9]*:?xmpmeta>/gi, "drop <xmpmeta> block");
  drop(/<rdf:RDF[\s\S]*?<\/rdf:RDF>/gi, "drop <rdf:RDF> block");
  drop(/<\?xpacket[\s\S]*?\?>/gi, "drop xpacket marker");
  drop(/<desc[\s\S]*?<\/desc>/gi, "drop <desc> block");
  out = out.replace(/<!--[\s\S]*?-->/g, (m) => {
    if (AI_META_NAME_RE.test(m)) { actions.push("drop AI-related comment"); return ""; }
    return m;
  });
  const scrubbed = cleanText(out, {});
  if (scrubbed.stats.removedCount) actions.push(`Layer A: ${scrubbed.stats.removedCount} invisible chars removed`);
  return { text: scrubbed.text, actions };
}

/* ------------------------------- HTML ------------------------------- */

function cleanHtmlText(src) {
  const actions = [];
  let out = src;
  out = out.replace(/<meta\b[^>]*>/gi, (m) => {
    if (AI_META_NAME_RE.test(m)) { actions.push(`drop meta tag: ${m.slice(0, 70)}`); return ""; }
    return m;
  });
  out = out.replace(/<script\b[^>]*type=["']application\/ld\+json["'][^>]*>[\s\S]*?<\/script>/gi, (m) => {
    if (AI_META_NAME_RE.test(m)) { actions.push("drop JSON-LD provenance block"); return ""; }
    return m;
  });
  const beforeAttrs = out;
  out = out.replace(/\sdata-ai[a-z-]*=("[^"]*"|'[^']*')/gi, "");
  if (out !== beforeAttrs) actions.push("drop data-ai* attributes");
  out = out.replace(/<!--[\s\S]*?-->/g, (m) => {
    if (AI_META_NAME_RE.test(m)) { actions.push("drop AI-related comment"); return ""; }
    return m;
  });
  const scrubbed = cleanText(out, {});
  if (scrubbed.stats.removedCount) actions.push(`Layer A: ${scrubbed.stats.removedCount} invisible chars removed`);
  return { text: scrubbed.text, actions };
}

/* ----------------------------- Markdown ----------------------------- */

function cleanMarkdownText(src) {
  const actions = [];
  let body = src;
  let front = null;
  const m = src.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
  if (m) { front = m[1]; body = src.slice(m[0].length); }
  let newFront = null;
  if (front !== null) {
    const kept = [];
    let dropping = false;
    for (const line of front.split(/\r?\n/)) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith("#")) { if (!dropping) kept.push(line); continue; }
      const indented = /^\s/.test(line);
      if (indented || trimmed.startsWith("- ")) { if (!dropping) kept.push(line); continue; }
      const key = (trimmed.split(":")[0] || "").trim().toLowerCase();
      if (AI_FRONTMATTER_KEYS.has(key)) { dropping = true; actions.push(`drop frontmatter key: ${key}`); continue; }
      dropping = false;
      kept.push(line);
    }
    newFront = kept.join("\n");
  }
  const scrubbed = cleanText(body, {});
  if (scrubbed.stats.removedCount) actions.push(`Layer A: ${scrubbed.stats.removedCount} invisible chars removed`);
  const text = newFront !== null && newFront.trim()
    ? `---\n${newFront}\n---\n${scrubbed.text}`
    : scrubbed.text;
  return { text, actions };
}

function inspectMarkdown(src) {
  const findings = [];
  const m = src.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
  if (m) {
    for (const line of m[1].split(/\r?\n/)) {
      const trimmed = line.trim();
      if (!trimmed || /^\s/.test(line) || trimmed.startsWith("#")) continue;
      const key = (trimmed.split(":")[0] || "").trim().toLowerCase();
      if (AI_FRONTMATTER_KEYS.has(key)) findings.push(`frontmatter AI key: ${key}`);
    }
  }
  const rep = inspectText(src, {});
  if (rep.suspiciousTotal) findings.push(`${rep.suspiciousTotal} invisible/format Unicode chars (Layer A)`);
  return findings;
}

/* -------------------------------- PDF -------------------------------- */
// Byte-length-preserving scrub: XMP packets and Info-dictionary values are
// overwritten with spaces so xref offsets stay valid.

function scrubPdf(u8) {
  const actions = [];
  const out = new Uint8Array(u8);
  const SP = 0x20;
  const blank = (from, to) => { for (let i = from; i < to; i++) out[i] = SP; };

  let searchFrom = 0;
  for (;;) {
    const begin = indexOfBytes(out, "<?xpacket begin", searchFrom);
    if (begin === -1) break;
    let end = indexOfBytes(out, "<?xpacket end", begin);
    if (end === -1) { end = begin + 16; }
    else {
      const close = indexOfBytes(out, "?>", end);
      end = close === -1 ? end : close + 2;
    }
    blank(begin, end);
    actions.push("blank XMP metadata packet");
    searchFrom = end;
  }

  // Standalone <x:xmpmeta> blocks (uncompressed)
  searchFrom = 0;
  for (;;) {
    const begin = indexOfBytes(out, "<x:xmpmeta", searchFrom);
    if (begin === -1) break;
    const close = indexOfBytes(out, "</x:xmpmeta>", begin);
    if (close === -1) break;
    blank(begin, close + 12);
    actions.push("blank <x:xmpmeta> block");
    searchFrom = close + 12;
  }

  const INFO_KEYS = ["/Producer", "/Creator", "/CreationDate", "/ModDate", "/Author", "/Title", "/Subject", "/Keywords"];
  for (const key of INFO_KEYS) {
    let from = 0;
    for (;;) {
      const at = indexOfBytes(out, key, from);
      if (at === -1) break;
      let p = at + key.length;
      while (p < out.length && (out[p] === 0x20 || out[p] === 0x0a || out[p] === 0x0d)) p++;
      if (out[p] === 0x28) { // ( literal string )
        let q = p + 1;
        let depth = 1;
        while (q < out.length && depth > 0) {
          if (out[q] === 0x5c) { q += 2; continue; }
          if (out[q] === 0x28) depth++;
          else if (out[q] === 0x29) depth--;
          q++;
        }
        blank(p + 1, q - 1);
        actions.push(`blank ${key} value`);
        from = q;
      } else if (out[p] === 0x3c && out[p + 1] !== 0x3c) { // <hex string>
        let q = p + 1;
        while (q < out.length && out[q] !== 0x3e) q++;
        for (let i = p + 1; i < q; i++) out[i] = 0x30;
        actions.push(`blank ${key} value`);
        from = q;
      } else {
        from = p + 1;
      }
    }
  }
  if (!actions.length) actions.push("no uncompressed PDF metadata found (already clean or compressed)");
  return { bytes: out, actions };
}

/* --------------------------- DOCX / ODT ------------------------------ */

function scrubXmlValues(xml, tags) {
  let changed = false;
  for (const tag of tags) {
    const re = new RegExp(`(<${tag}\\b[^>]*>)([\\s\\S]*?)(</${tag}>)`, "gi");
    xml = xml.replace(re, (m, open, inner, close) => {
      if (!inner) return m;
      changed = true;
      return `${open}${close}`;
    });
  }
  return { xml, changed };
}

const CORE_TAGS = ["dc:creator", "dc:title", "dc:subject", "dc:description", "cp:keywords", "cp:lastModifiedBy", "cp:category", "cp:contentStatus", "cp:revision"];
const APP_TAGS = ["Application", "AppVersion", "Company", "Manager", "Template", "TotalTime"];

function cleanZipDoc(u8, kind) {
  const actions = [];
  const files = unzipSync(u8);
  const outFiles = {};
  const dropped = [];

  for (const [name, data] of Object.entries(files)) {
    if (kind === "docx") {
      if (name.startsWith("customXml/") || /^docProps\/thumbnail\./i.test(name)) {
        dropped.push(name);
        actions.push(`drop ${name}`);
        continue;
      }
      if (name === "docProps/core.xml" || name === "docProps/app.xml") {
        const tags = name.endsWith("core.xml") ? CORE_TAGS : APP_TAGS;
        const res = scrubXmlValues(utf8Decode(data), tags);
        if (res.changed) actions.push(`clear document properties in ${name}`);
        outFiles[name] = utf8Encode(res.xml);
        continue;
      }
    }
    if (kind === "odt" && name === "meta.xml") {
      let xml = utf8Decode(data);
      const before = xml;
      xml = xml.replace(/<meta:generator>[\s\S]*?<\/meta:generator>/gi, "");
      xml = xml.replace(/<dc:creator>[\s\S]*?<\/dc:creator>/gi, "");
      xml = xml.replace(/<meta:initial-creator>[\s\S]*?<\/meta:initial-creator>/gi, "");
      if (xml !== before) actions.push("scrub meta.xml generator/creator");
      outFiles[name] = utf8Encode(xml);
      continue;
    }
    if (name.endsWith(".xml") || name.endsWith(".rels")) {
      const scrubbed = cleanText(utf8Decode(data), {});
      if (scrubbed.stats.removedCount) actions.push(`Layer A scrub in ${name} (${scrubbed.stats.removedCount})`);
      outFiles[name] = utf8Encode(scrubbed.text);
      continue;
    }
    outFiles[name] = data;
  }

  // Keep the package consistent: drop relationships and content-type overrides
  // that point at the parts we removed.
  if (dropped.length) {
    for (const relName of Object.keys(outFiles)) {
      if (!relName.endsWith(".rels")) continue;
      let xml = utf8Decode(outFiles[relName]);
      const before = xml;
      xml = xml.replace(/<Relationship\b[^>]*\/>/gi, (m) => {
        const target = (m.match(/Target="([^"]+)"/i) || [])[1] || "";
        const norm = target.replace(/^\/+/, "").replace(/^\.\.\//, "");
        return dropped.some((d) => d === norm || d.endsWith(norm)) ? "" : m;
      });
      if (xml !== before) outFiles[relName] = utf8Encode(xml);
    }
    const ct = "[Content_Types].xml";
    if (outFiles[ct]) {
      let xml = utf8Decode(outFiles[ct]);
      xml = xml.replace(/<Override\b[^>]*\/>/gi, (m) => {
        const part = ((m.match(/PartName="([^"]+)"/i) || [])[1] || "").replace(/^\//, "");
        return dropped.includes(part) ? "" : m;
      });
      outFiles[ct] = utf8Encode(xml);
    }
  }

  if (!actions.length) actions.push("no document properties found (already clean)");
  return { bytes: zipSync(outFiles, { level: 6 }), actions };
}

function inspectZipDoc(u8, kind) {
  const findings = [];
  const files = unzipSync(u8);
  for (const [name, data] of Object.entries(files)) {
    if (kind === "docx") {
      if (name === "docProps/core.xml" || name === "docProps/app.xml") {
        const xml = utf8Decode(data);
        const tags = name.endsWith("core.xml") ? CORE_TAGS : APP_TAGS;
        const filled = tags.filter((tag) => {
          const m = xml.match(new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)</${tag}>`, "i"));
          return m && m[1].trim();
        });
        for (const tag of filled) {
          const m = xml.match(new RegExp(`<${tag}\\b[^>]*>([\\s\\S]*?)</${tag}>`, "i"));
          findings.push(`${name} ${tag}: ${m[1].trim().slice(0, 50)}`);
        }
        continue;
      }
      if (name.startsWith("customXml/") || /^docProps\/thumbnail\./i.test(name)) {
        findings.push(`document part: ${name}`);
        continue;
      }
    }
    if (kind === "odt" && name === "meta.xml") {
      findings.push("meta.xml present (generator / creator metadata)");
      const hits = containsAny(data, ALL_HINTS);
      if (hits.length) findings.push(`meta.xml: ${hits.slice(0, 6).join(", ")}`);
    }
  }
  return findings;
}

/* ---------------------------- dispatchers ---------------------------- */

export function inspectContainer(format, u8) {
  const findings = [];
  let { hasC2pa, hasAi, hits } = blobHits(u8);
  // In readable documents these markers are ordinary visible words: we report
  // them but we never rewrite the user's content behind their back.
  const readable = ["text", "markdown", "html", "svg"].includes(format);
  if (hits.length) {
    findings.push(
      readable
        ? `info: visible wording kept in the content: ${hits.slice(0, 8).join(", ")}`
        : `byte-scan markers: ${hits.slice(0, 8).join(", ")}`
    );
    if (readable) hasC2pa = false;
  }
  try {
    if (format === "svg" || format === "html" || format === "markdown" || format === "text") {
      const src = utf8Decode(u8);
      if (format === "markdown") findings.push(...inspectMarkdown(src));
      else {
        if (format === "svg") {
          if (/<metadata[\s>]/i.test(src)) findings.push("<metadata> block present");
          if (/xmpmeta/i.test(src)) findings.push("XMP packet present");
        }
        if (format === "html") {
          const metas = src.match(/<meta\b[^>]*>/gi) || [];
          for (const m of metas) if (AI_META_NAME_RE.test(m)) findings.push(`meta tag: ${m.slice(0, 70)}`);
        }
        const rep = inspectText(src, {});
        if (rep.suspiciousTotal) findings.push(`${rep.suspiciousTotal} invisible/format Unicode chars (Layer A)`);
        if (rep.suspiciousTotal) hasAi = true;
      }
    } else if (format === "pdf") {
      const xmp = indexOfBytes(u8, "<?xpacket begin");
      if (xmp !== -1) {
        const chunk = latin1(u8, xmp, Math.min(xmp + 4000, u8.length));
        if (/<[a-z]/i.test(chunk.slice(60))) findings.push("XMP metadata packet present");
      }
      for (const key of ["/Producer", "/Creator", "/Author", "/Title", "/Subject", "/Keywords"]) {
        const at = indexOfBytes(u8, key);
        if (at === -1) continue;
        const raw = latin1(u8, at + key.length, Math.min(at + key.length + 200, u8.length));
        const m = raw.match(/^\s*\(([^)]*)\)/);
        const value = m ? m[1].trim() : "";
        if (value) findings.push(`${key}: ${value.slice(0, 60)}`);
      }
    } else if (format === "docx" || format === "odt") {
      findings.push(...inspectZipDoc(u8, format));
    }
  } catch (e) {
    findings.push(`inspection error: ${e.message}`);
  }
  if (findings.length && !hasAi) hasAi = true;
  return { hasC2pa, hasAi, findings };
}

export function cleanContainer(format, u8) {
  if (format === "pdf") return scrubPdf(u8);
  if (format === "docx" || format === "odt") return cleanZipDoc(u8, format);
  const src = utf8Decode(u8);
  let res;
  if (format === "svg") res = cleanSvgText(src);
  else if (format === "html") res = cleanHtmlText(src);
  else if (format === "markdown") res = cleanMarkdownText(src);
  else {
    const scrubbed = cleanText(src, {});
    res = { text: scrubbed.text, actions: scrubbed.stats.removedCount ? [`Layer A: ${scrubbed.stats.removedCount} invisible chars removed`] : ["no invisible Unicode found"] };
  }
  return { bytes: utf8Encode(res.text), actions: res.actions };
}
